import { Scissors } from 'lucide-react'

import type { PublicPatient } from '@snootsvet/shared/portal'

import {
  EditActions,
  EditableField,
  EditableValue,
  EditButton,
  useEditableField,
} from '@/customer/components/editable-field'
import { RadioGroup, RadioOption } from '@/customer/components/ui/radio-group'
import { useUpdatePet } from '@/customer/hooks/use-update-pet'

/**
 * Radio option keys - `RadioGroup` values are strings, so we map
 * to/from `isNeutered: boolean | null` at the boundary.
 */
type NeuterKey = 'yes' | 'no' | 'unknown'

function valueToKey(value: boolean | null): NeuterKey {
  if (value === true) return 'yes'
  if (value === false) return 'no'
  return 'unknown'
}

function keyToValue(key: NeuterKey): boolean | null {
  if (key === 'yes') return true
  if (key === 'no') return false
  return null
}

const NEUTER_LABELS: Record<NeuterKey, string> = {
  yes: 'Yes',
  no: 'No',
  unknown: 'Not sure',
}

/**
 * Sex-appropriate row label. Consumer-facing UK convention (RSPCA /
 * PDSA / Blue Cross): "Spayed" for female, "Neutered" for male and
 * unknown. "Castrated" is technically correct for males but reads
 * as needlessly clinical for a member portal.
 */
function rowLabel(sex: PublicPatient['sex']): string {
  return sex === 'female' ? 'Spayed' : 'Neutered'
}

/**
 * Neuter status row. Not asked for during registration - members
 * usually record this once they've had the procedure done, or leave
 * it unset. Always editable. Row label tracks `pet.sex` so male pets
 * see "Castrated", female pets "Spayed", unknown-sex falls back to
 * the generic "Neutered".
 */
export function NeuterPetField({ pet }: { pet: PublicPatient }) {
  const updatePet = useUpdatePet(pet.id)
  const currentKey = valueToKey(pet.isNeutered)
  const neuterField = useEditableField<NeuterKey>({
    initialValue: currentKey,
    onSave: async ({ value, setError }) => {
      if (value === currentKey) return
      try {
        await updatePet.mutateAsync({ isNeutered: keyToValue(value) })
      } catch {
        setError('Something went wrong saving. Please try again.')
      }
    },
  })

  const label = rowLabel(pet.sex)

  return (
    <EditableField
      label={label}
      icon={Scissors}
      error={neuterField.error}
      action={<EditButton field={neuterField} label={label.toLowerCase()} />}
    >
      {neuterField.isEditing ? (
        <RadioGroup
          value={neuterField.draft}
          onValueChange={(v) => neuterField.setDraft(v as NeuterKey)}
        >
          <RadioOption value="yes">Yes</RadioOption>
          <RadioOption value="no">No</RadioOption>
          <RadioOption value="unknown">Not sure</RadioOption>
        </RadioGroup>
      ) : (
        <EditableValue>
          {pet.isNeutered === null ? (
            <span className="text-muted-foreground">Not set</span>
          ) : (
            NEUTER_LABELS[currentKey]
          )}
        </EditableValue>
      )}
      <EditActions field={neuterField} />
    </EditableField>
  )
}
